import { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas, Circle, Text, Line, FabricImage } from 'fabric';
import { Info, Lock, Unlock } from 'lucide-react';
import { getEventCoords, applyTouchActionNone, attachPinchZoom } from '../../../utils/canvasTouchUtils';
import { useElementSize } from '../../../hooks/useElementSize';
import { calculateNativePixelsPerMeter } from './scaleCalibrationUtils';

// Padding inside the canvas reserved around the image on initial fit.
const IMAGE_FIT_PADDING_PX = 40;
// Floor for canvas dimensions so the surface stays usable on tiny viewports.
const MIN_CANVAS_DIMENSION_PX = 200;

/**
 * ScaleCalibrator Component
 *
 * Interactive Fabric.js canvas for 2-point scale calibration of background images.
 * Design based on: docs/design/screenshots/05_Dialog_Hintergrundbild_Import_Picture_nachUpload.png
 *
 * Features:
 * - Image display with built-in drag and rotation (Fabric.js controls)
 * - Click to place 2 calibration points
 * - Green markers with numbers "1" and "2"
 * - Dashed line connecting markers
 * - Pixel distance calculation
 * - Markers are draggable to adjust calibration
 *
 * @param {Object} props
 * @param {string} props.dataUrl - Base64 data URL of the uploaded image
 * @param {Object} props.calibration - Calibration state { point1, point2, pixelDistance, realDistance, scale, isCalibrated }
 * @param {number} props.transparency - Image transparency 0-100%
 * @param {Function} props.onCalibrationChange - Callback when calibration changes
 * @param {Function} props.onReset - Callback to reset calibration
 * @param {Function} props.onImageTransform - Callback when image position/rotation changes
 */
const ScaleCalibrator = ({ dataUrl, calibration, transparency, onCalibrationChange, onReset, onImageTransform }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [canvasWrapperRef, wrapperSize] = useElementSize();
  const imageObjectRef = useRef(null);
  const marker1Ref = useRef(null);
  const marker2Ref = useRef(null);
  const calibrationLineRef = useRef(null);
  const distanceLabelRef = useRef(null);
  const calibrationRef = useRef(calibration); // Keep latest calibration
  const lastPosRef = useRef({ x: 0, y: 0 });

  const [isPlacingPoint, setIsPlacingPoint] = useState(false);
  const [nextPointNumber, setNextPointNumber] = useState(1);
  const [isImageLocked, setIsImageLocked] = useState(true); // Start locked for calibration
  // Pan-active flag as a ref, not useState: the Fabric mouse:move handler reads it
  // live, so a setIsPanning(true) in mouse:down would otherwise be invisible to the
  // already-registered move handler (stale closure) until React re-renders. Native
  // touchmove events arrive before that re-render, which broke touch-pan (#487).
  const isPanningRef = useRef(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // eslint-disable-line no-unused-vars
  const onImageTransformRef = useRef(onImageTransform);

  // Update calibration ref whenever props change
  useEffect(() => {
    calibrationRef.current = calibration;
  }, [calibration]);

  // Update onImageTransform ref whenever it changes
  useEffect(() => {
    onImageTransformRef.current = onImageTransform;
  }, [onImageTransform]);

  /**
   * Initialize Fabric.js canvas. Dimensions are seeded from a viewport
   * approximation (the CSS sizes the wrapper to ~60vh × dialog width) so the
   * first image-fit lands close to its final size — the resize effect below
   * then snaps the canvas to the wrapper's measured size (#450).
   */
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const initWidth = Math.max(
      MIN_CANVAS_DIMENSION_PX,
      Math.min(1400, Math.round(window.innerWidth * 0.85)),
    );
    const initHeight = Math.max(
      MIN_CANVAS_DIMENSION_PX,
      Math.min(650, Math.round(window.innerHeight * 0.55)),
    );

    const canvas = new Canvas(canvasRef.current, {
      width: initWidth,
      height: initHeight,
      backgroundColor: '#F9FAFB', // Gray-50
      selection: true,  // Allow selection for image drag/rotate
    });

    fabricCanvasRef.current = canvas;
    applyTouchActionNone(canvas);

    // E2E hook: expose Fabric canvas so Playwright can read getZoom() and
    // viewportTransform (no DOM-level back-reference exists in Fabric v6).
    if (typeof window !== 'undefined') {
      window.__scaleCalibratorFabric = canvas;
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      if (typeof window !== 'undefined' && window.__scaleCalibratorFabric === canvas) {
        delete window.__scaleCalibratorFabric;
      }
    };
  }, []);

  /**
   * Keep the canvas surface in sync with the wrapper element so clicks land
   * exactly where the user sees the cursor (#450). Image and calibration
   * markers keep their original pixel coordinates; the user can pan/zoom if
   * the new viewport hides them.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !wrapperSize) return;
    const width = Math.max(MIN_CANVAS_DIMENSION_PX, Math.round(wrapperSize.width));
    const height = Math.max(MIN_CANVAS_DIMENSION_PX, Math.round(wrapperSize.height));
    if (canvas.width === width && canvas.height === height) return;
    canvas.setDimensions({ width, height });
    canvas.requestRenderAll();
  }, [wrapperSize]);

  /**
   * Load image onto canvas with Fabric.js drag and rotate controls
   */
  useEffect(() => {
    if (!fabricCanvasRef.current || !dataUrl) return;

    const canvas = fabricCanvasRef.current;

    // Use FabricImage.fromURL to load image from dataUrl
    FabricImage.fromURL(dataUrl, {
      crossOrigin: 'anonymous',
    }).then((fabricImage) => {
      // Fit image to current canvas dimensions (responsive — #450).
      // The image stays at this fit on subsequent canvas resizes so the user
      // doesn't lose calibration markers placed relative to it.
      const maxWidth = Math.max(1, canvas.width - IMAGE_FIT_PADDING_PX);
      const maxHeight = Math.max(1, canvas.height - IMAGE_FIT_PADDING_PX);
      const scale = Math.min(maxWidth / fabricImage.width, maxHeight / fabricImage.height);

      fabricImage.set({
        scaleX: scale,
        scaleY: scale,
        left: 20,       // upper-left corner with padding
        top: 20,        // upper-left corner with padding
        originX: 'left', // Anchor to upper-left
        originY: 'top',
        opacity: transparency / 100,
        selectable: false,       // Start locked for calibration
        lockScalingX: true,      // Disable scaling
        lockScalingY: true,      // Keep only drag & rotate
        hasControls: false,      // Start hidden
        hasBorders: false,
        evented: false,          // Prevent all mouse events when locked
      });

      canvas.add(fabricImage);
      canvas.sendObjectToBack(fabricImage);
      imageObjectRef.current = fabricImage;

      // Track image transformations (position, rotation)
      fabricImage.on('moving', () => {
        if (onImageTransformRef.current) {
          onImageTransformRef.current({
            position: { x: fabricImage.left, y: fabricImage.top },
            rotation: fabricImage.angle || 0,
          });
        }
      });

      fabricImage.on('rotating', () => {
        if (onImageTransformRef.current) {
          onImageTransformRef.current({
            position: { x: fabricImage.left, y: fabricImage.top },
            rotation: fabricImage.angle || 0,
          });
        }
      });

      canvas.requestRenderAll();
    });

    // Clean up image on unmount or dataUrl change
    return () => {
      if (imageObjectRef.current) {
        canvas.remove(imageObjectRef.current);
        imageObjectRef.current = null;
      }
    };
  }, [dataUrl, transparency]);

  /**
   * Update image transparency when transparency prop changes
   */
  useEffect(() => {
    if (imageObjectRef.current) {
      imageObjectRef.current.set({ opacity: transparency / 100 });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [transparency]);

  /**
   * Update image lock state (toggle between move/rotate mode and calibration mode)
   */
  useEffect(() => {
    if (imageObjectRef.current && fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      imageObjectRef.current.set({
        selectable: !isImageLocked,
        hasControls: !isImageLocked,
        hasBorders: !isImageLocked,
        evented: !isImageLocked,  // Prevent mouse events when locked
      });

      // Force canvas to recognize the change
      canvas.discardActiveObject();
      canvas.requestRenderAll();

      // Small delay to ensure canvas updates properly
      setTimeout(() => {
        canvas.requestRenderAll();
      }, 50);
    }
  }, [isImageLocked]);

  /**
   * Update distance label when calibration.realDistance changes externally
   */
  useEffect(() => {
    if (distanceLabelRef.current && calibration.isCalibrated && calibration.pixelDistance) {
      const realDistance = calibration.pixelDistance / calibration.scale;
      distanceLabelRef.current.set({
        text: `${realDistance.toFixed(2)} m`,
      });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [calibration.realDistance, calibration.scale, calibration.isCalibrated, calibration.pixelDistance]);

  /**
   * Create calibration marker (crosshair only, without label)
   * Label is created separately to avoid affecting the crosshair center position
   */
  const createCalibrationMarker = useCallback((x, y, number) => {
    const crosshairSize = 20;
    const lineWidth = 2;

    // Horizontal line
    const hLine = new Line(
      [x - crosshairSize / 2, y, x + crosshairSize / 2, y],
      {
        stroke: '#10B981',      // Emerald-500
        strokeWidth: lineWidth,
        selectable: false,
        evented: false,
      }
    );

    // Vertical line
    const vLine = new Line(
      [x, y - crosshairSize / 2, x, y + crosshairSize / 2],
      {
        stroke: '#10B981',      // Emerald-500
        strokeWidth: lineWidth,
        selectable: false,
        evented: false,
      }
    );

    // Center dot for precise placement
    const centerDot = new Circle({
      radius: 3,
      fill: '#10B981',      // Emerald-500
      stroke: '#FFFFFF',
      strokeWidth: 1,
      left: x,
      top: y,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });

    // Invisible circle for easier dragging
    const dragHandle = new Circle({
      radius: 15,
      fill: 'transparent',
      left: x,
      top: y,
      originX: 'center',
      originY: 'center',
      selectable: true,
      hasControls: false,
      hasBorders: false,
      lockRotation: true,
      hoverCursor: 'move',
    });

    // Number label with background (separate, not grouped)
    const labelBg = new Circle({
      radius: 10,
      fill: '#10B981',      // Emerald-500
      stroke: '#FFFFFF',
      strokeWidth: 2,
      left: x + 22,
      top: y - 22,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });

    const label = new Text(number.toString(), {
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      fontWeight: 'bold',
      fill: '#FFFFFF',
      left: x + 22,
      top: y - 22,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });

    // Store references in the drag handle
    dragHandle.set({
      data: {
        hLine,
        vLine,
        centerDot,
        labelBg,
        label,
      },
    });

    return { dragHandle, hLine, vLine, centerDot, labelBg, label };
  }, []);

  /**
   * Update calibration when markers are moved
   */
  const updateCalibrationFromMarkers = useCallback(() => {
    if (!marker1Ref.current || !marker2Ref.current) return;

    const marker1 = marker1Ref.current;
    const marker2 = marker2Ref.current;

    const x1 = marker1.left;
    const y1 = marker1.top;
    const x2 = marker2.left;
    const y2 = marker2.top;

    // Update crosshair components for marker 1
    const data1 = marker1.data;
    if (data1) {
      data1.hLine.set({ x1: x1 - 10, y1, x2: x1 + 10, y2: y1 });
      data1.vLine.set({ x1, y1: y1 - 10, x2: x1, y2: y1 + 10 });
      data1.centerDot.set({ left: x1, top: y1 });
      data1.labelBg.set({ left: x1 + 22, top: y1 - 22 });
      data1.label.set({ left: x1 + 22, top: y1 - 22 });
    }

    // Update crosshair components for marker 2
    const data2 = marker2.data;
    if (data2) {
      data2.hLine.set({ x1: x2 - 10, y1: y2, x2: x2 + 10, y2 });
      data2.vLine.set({ x1: x2, y1: y2 - 10, x2, y2: y2 + 10 });
      data2.centerDot.set({ left: x2, top: y2 });
      data2.labelBg.set({ left: x2 + 22, top: y2 - 22 });
      data2.label.set({ left: x2 + 22, top: y2 - 22 });
    }

    // Use latest calibration from ref to avoid stale closure
    const currentCalibration = calibrationRef.current;

    // Recalculate pixel distance and scale
    const dx = x2 - x1;
    const dy = y2 - y1;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);
    const scale = pixelDistance / currentCalibration.realDistance;
    const imageFitScale = imageObjectRef.current?.scaleX;
    const nativePixelsPerMeter = calculateNativePixelsPerMeter(
      pixelDistance,
      currentCalibration.realDistance,
      imageFitScale,
    );

    // Update connecting line position
    if (calibrationLineRef.current) {
      calibrationLineRef.current.set({
        x1,
        y1,
        x2,
        y2,
      });
      calibrationLineRef.current.setCoords();
    }

    // Update distance label position and text
    if (distanceLabelRef.current) {
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;

      distanceLabelRef.current.set({
        left: midX,
        top: midY - 20,
        text: `${currentCalibration.realDistance.toFixed(2)} m`,
      });
      distanceLabelRef.current.setCoords();
    }

    // Only update the fields that changed from dragging
    onCalibrationChange({
      point1: { x: x1, y: y1 },
      point2: { x: x2, y: y2 },
      pixelDistance,
      realDistance: currentCalibration.realDistance, // Use latest from ref
      scale,
      nativePixelsPerMeter,
      isCalibrated: true,
    });

    fabricCanvasRef.current?.requestRenderAll();
  }, [onCalibrationChange]);

  /**
   * Handle canvas click for point placement
   */
  const handleCanvasClick = useCallback((e) => {
    if (!isPlacingPoint || !fabricCanvasRef.current) return;

    // Don't interfere when image is unlocked (allow dragging)
    if (!isImageLocked) return;

    // Ignore clicks on existing objects
    if (e.target) return;

    const canvas = fabricCanvasRef.current;
    const pointer = canvas.getScenePoint(e.e);

    if (nextPointNumber === 1) {
      // Place first marker
      const { dragHandle, hLine, vLine, centerDot, labelBg, label } = createCalibrationMarker(pointer.x, pointer.y, 1);

      // Add all components to canvas
      canvas.add(hLine, vLine, centerDot, labelBg, label, dragHandle);

      // Attach moving event to drag handle
      dragHandle.on('moving', updateCalibrationFromMarkers);

      marker1Ref.current = dragHandle;
      setNextPointNumber(2);

      onCalibrationChange({
        ...calibration,
        point1: { x: pointer.x, y: pointer.y },
        isCalibrated: false,
      });
    } else {
      // Place second marker
      const { dragHandle, hLine, vLine, centerDot, labelBg, label } = createCalibrationMarker(pointer.x, pointer.y, 2);

      // Add all components to canvas
      canvas.add(hLine, vLine, centerDot, labelBg, label, dragHandle);

      // Attach moving event to drag handle
      dragHandle.on('moving', updateCalibrationFromMarkers);

      marker2Ref.current = dragHandle;

      // Calculate pixel distance and scale
      const dx = pointer.x - marker1Ref.current.left;
      const dy = pointer.y - marker1Ref.current.top;
      const pixelDistance = Math.sqrt(dx * dx + dy * dy);
      const scale = pixelDistance / calibration.realDistance;
      const imageFitScale = imageObjectRef.current?.scaleX;
      const nativePixelsPerMeter = calculateNativePixelsPerMeter(
        pixelDistance,
        calibration.realDistance,
        imageFitScale,
      );

      // Draw connecting line
      const line = new Line(
        [marker1Ref.current.left, marker1Ref.current.top, pointer.x, pointer.y],
        {
          stroke: '#10B981',      // Emerald-500
          strokeWidth: 2,
          strokeDashArray: [5, 5],
          selectable: false,
          evented: false,
          data: { type: 'calibration-line' },
        }
      );
      canvas.add(line);
      canvas.sendObjectToBack(line);
      calibrationLineRef.current = line;

      // Create distance label on the line
      const midX = (marker1Ref.current.left + pointer.x) / 2;
      const midY = (marker1Ref.current.top + pointer.y) / 2;

      const distanceLabel = new Text(`${calibration.realDistance.toFixed(2)} m`, {
        left: midX,
        top: midY - 20,
        fontSize: 14,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 4,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      });
      canvas.add(distanceLabel);
      distanceLabelRef.current = distanceLabel;

      onCalibrationChange({
        ...calibration,
        point2: { x: pointer.x, y: pointer.y },
        pixelDistance,
        scale,
        nativePixelsPerMeter,
        isCalibrated: true,
      });

      setIsPlacingPoint(false);
      setNextPointNumber(1);
    }

    canvas.requestRenderAll();
  }, [isPlacingPoint, isImageLocked, nextPointNumber, calibration, createCalibrationMarker, onCalibrationChange, updateCalibrationFromMarkers]);

  /**
   * Pan handlers for spacebar + drag and single-finger touch pan
   */
  const handlePanMouseDown = useCallback((opt) => {
    if (spacePressed) {
      isPanningRef.current = true;
      const { x, y } = getEventCoords(opt.e);
      lastPosRef.current = { x, y };
    }
  }, [spacePressed]);

  const handlePanMouseMove = useCallback((opt) => {
    if (!isPanningRef.current) return;
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const { x, y } = getEventCoords(opt.e);
    if (x == null || Number.isNaN(x) || y == null || Number.isNaN(y)) return;
    const dx = x - lastPosRef.current.x;
    const dy = y - lastPosRef.current.y;
    canvas.relativePan({ x: dx, y: dy });
    canvas.requestRenderAll();
    lastPosRef.current = { x, y };
  }, []);

  const handlePanMouseUp = useCallback(() => {
    isPanningRef.current = false;
  }, []);

  /**
   * Attach mouse event handlers (pan takes priority over calibration point placement)
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const handleMouseDown = (opt) => {
      const isTouch =
        opt.e.pointerType === 'touch' ||
        (typeof TouchEvent !== 'undefined' && opt.e instanceof TouchEvent);
      if (isTouch && !opt.target && isImageLocked && !isPlacingPoint) {
        isPanningRef.current = true;
        const { x, y } = getEventCoords(opt.e);
        lastPosRef.current = { x, y };
        return;
      }
      // Middle-button pan (#451) — works regardless of spacePressed, mirroring GardenCanvas.
      if (opt.e.button === 1) {
        isPanningRef.current = true;
        const { x, y } = getEventCoords(opt.e);
        lastPosRef.current = { x, y };
        canvas.setCursor('grabbing');
        opt.e.preventDefault();
        return;
      }
      if (spacePressed) {
        handlePanMouseDown(opt);
      } else {
        handleCanvasClick(opt);
      }
    };

    const handleMouseMove = (opt) => {
      if (isPanningRef.current) {
        handlePanMouseMove(opt);
      }
    };

    const handleMouseUp = () => {
      if (isPanningRef.current) {
        handlePanMouseUp();
        canvas.setCursor(spacePressed ? 'grab' : 'default');
      }
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [handleCanvasClick, handlePanMouseDown, handlePanMouseMove, handlePanMouseUp, spacePressed, isImageLocked, isPlacingPoint]);

  /**
   * Handle spacebar for panning (window-level, matching Step3GardenOutline)
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) ||
                       document.activeElement?.contentEditable === 'true';
      if (isTyping) return;
      if (e.key === ' ') {
        e.preventDefault();
        setSpacePressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === ' ') {
        setSpacePressed(false);
        isPanningRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  /**
   * Two-finger pinch-to-zoom on touch devices (#746). Fabric dropped its
   * `touch:gesture` event after v5 — pinch is wired onto native touchmove.
   */
  useEffect(() => {
    return attachPinchZoom(fabricCanvasRef.current, { onZoom: setZoomLevel });
  }, []);

  /**
   * Mouse-wheel zoom (#451). Mirrors GardenCanvas mouse:wheel — clamp [0.1, 5],
   * zoom toward the cursor so the point under the pointer stays anchored.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const handler = (opt) => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      zoom = Math.max(0.1, Math.min(5, zoom));
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      setZoomLevel(zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    };
    canvas.on('mouse:wheel', handler);
    return () => canvas.off('mouse:wheel', handler);
  }, []);

  /**
   * Cancel an active pan when a second finger is placed (two-finger gesture starts).
   * Prevents pan-state leaking into pinch-zoom.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    const upper = canvas?.upperCanvasEl;
    if (!upper) return;
    const onTouchStart = (e) => {
      if ((e.touches?.length ?? 0) >= 2) isPanningRef.current = false;
    };
    upper.addEventListener('touchstart', onTouchStart, { passive: true });
    return () => upper.removeEventListener('touchstart', onTouchStart);
  }, []);

  /**
   * Handle "Punkte setzen" button click
   */
  const handleStartPlacing = () => {
    setIsPlacingPoint(true);
    setNextPointNumber(1);
  };

  /**
   * Handle "Neu kalibrieren" button click
   */
  const handleResetCalibration = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Remove marker 1 components
    if (marker1Ref.current) {
      const data1 = marker1Ref.current.data;
      if (data1) {
        canvas.remove(data1.hLine, data1.vLine, data1.centerDot, data1.labelBg, data1.label);
      }
      canvas.remove(marker1Ref.current);
      marker1Ref.current = null;
    }

    // Remove marker 2 components
    if (marker2Ref.current) {
      const data2 = marker2Ref.current.data;
      if (data2) {
        canvas.remove(data2.hLine, data2.vLine, data2.centerDot, data2.labelBg, data2.label);
      }
      canvas.remove(marker2Ref.current);
      marker2Ref.current = null;
    }

    // Remove connecting line
    if (calibrationLineRef.current) {
      canvas.remove(calibrationLineRef.current);
      calibrationLineRef.current = null;
    }

    // Remove distance label
    if (distanceLabelRef.current) {
      canvas.remove(distanceLabelRef.current);
      distanceLabelRef.current = null;
    }

    canvas.requestRenderAll();

    setIsPlacingPoint(false);
    setNextPointNumber(1);
    onReset();
  };

  /**
   * Zoom functions (matching Step3GardenOutline design)
   */
  const handleZoomIn = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 1.1;
    if (zoom > 5) zoom = 5;
    canvas.setZoom(zoom);
    setZoomLevel(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomOut = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 0.9;
    if (zoom < 0.1) zoom = 0.1;
    canvas.setZoom(zoom);
    setZoomLevel(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomReset = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    canvas.setZoom(1);
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    setZoomLevel(1);
    canvas.requestRenderAll();
  };

  return (
    <div className="space-y-3">
      {/* Info Badge */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-blue-800">
            Setze 2 Punkte auf dem Bild mit bekannter Distanz
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Leertaste + Ziehen: Canvas verschieben
          </p>
          <div className="flex gap-2 mt-2 flex-wrap">
            {/* Lock/Unlock Button */}
            <button
              onClick={() => setIsImageLocked(!isImageLocked)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                isImageLocked
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {isImageLocked ? (
                <>
                  <Lock size={14} />
                  <span>Gesperrt</span>
                </>
              ) : (
                <>
                  <Unlock size={14} />
                  <span>Entsperrt</span>
                </>
              )}
            </button>

            {/* Calibration Buttons */}
            {!calibration.isCalibrated && (
              <button
                onClick={handleStartPlacing}
                disabled={isPlacingPoint || !isImageLocked}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  isPlacingPoint
                    ? 'bg-blue-200 text-blue-600 cursor-not-allowed'
                    : !isImageLocked
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title={!isImageLocked ? 'Bitte sperren Sie zuerst das Bild' : ''}
              >
                {isPlacingPoint
                  ? `Klicken Sie Punkt ${nextPointNumber}`
                  : 'Punkte setzen'}
              </button>
            )}
            {calibration.isCalibrated && (
              <button
                onClick={handleResetCalibration}
                className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 text-gray-700
                           hover:bg-gray-300 transition-colors"
              >
                Neu kalibrieren
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas — sized via wrapper element so the Fabric surface stays inside
          the dialog viewport on every laptop resolution (#450). useElementSize
          measures the wrapper and the canvas is resized to match. */}
      <div
        ref={canvasWrapperRef}
        className="scale-calibrator-wrapper relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden"
        style={{
          width: '100%',
          // var(--sc-max-h) is capped on touch + short viewports (#486); it
          // falls back to 100vh on desktop, leaving min(60vh, 650px) intact.
          height: 'min(60vh, 650px, var(--sc-max-h, 100vh))',
          minHeight: '280px',
        }}
      >
        <canvas ref={canvasRef} />

        {/* Zoom Controls - Lower Right (matching Step3GardenOutline design) */}
        <div style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
        }}>
          <button
            onClick={handleZoomIn}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            aria-label="Zoom in"
            data-testid="scale-zoom-in"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            aria-label="Zoom out"
            data-testid="scale-zoom-out"
          >
            −
          </button>
          <button
            onClick={handleZoomReset}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '10px',
              transition: 'all 0.2s',
            }}
            aria-label="Reset zoom"
            data-testid="scale-zoom-reset"
          >
            1:1
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScaleCalibrator;
